// @ts-check
import classNames from 'classnames';
import { navigate } from 'gatsby';
import React from 'react';
import AccordionMenu from '~/components/Common/AccordionMenu';
import Link from '~/components/Common/Link';
import { GetCopyFromJson } from '~/contentstack/rendering';
import SvgImage from '~/templates/components/images/SvgImage';

import './Footer.scss';

/**
 * @typedef FooterTopLogo
 * @property {import('~/templates/CSTypes').CSLink} link
 * @property {import('~/templates/CSTypes').CSFile} logo
 *
 * @typedef FooterCopy
 * @property {import('~/templates/CSTypes').CSJRTE[]} copy
 *
 * @typedef FooterCenterColumn
 * @property {string} header
 * @property {import('~/templates/CSTypes').CSLink[]} link
 *
 * @typedef FooterSocialsIcons
 * @property {import('~/templates/CSTypes').CSLink} link
 * @property {import('~/templates/CSTypes').CSFile} icon
 *
 * @typedef FooterBankLogos
 * @property {string} alt_text
 * @property {import('~/templates/CSTypes').CSFile} logo
 *
 * @typedef FooterOptions
 * @property {FooterTopLogo} top_logo
 * @property {FooterCopy} pre_copy
 * @property {FooterCenterColumn[]} center_columns
 * @property {FooterCopy} post_copy
 * @property {FooterSocialsIcons[]} socials_icons
 * @property {FooterBankLogos[]} bank_logos
 *
 * @typedef FooterProps
 * @property {FooterOptions} options
 */

/** @type {import('~/types/React').ReactFC<FooterProps>} */
const Footer = ({ options, ...rest }) => {
	if (!options) {
		return null;
	}

	const pre_copy = GetCopyFromJson(options.pre_copy.copy);
	const post_copy = GetCopyFromJson(options.post_copy.copy);

	const goGoJuice = () => navigate(options?.top_logo?.link?.href);

	return (
		<div {...rest} className={classNames(rest.className, 'ozk-footer')}>
			<div className="top-logo" onClick={goGoJuice}>
				<Link to={options?.top_logo?.link?.href} title={options?.top_logo?.link?.title}>
					<SvgImage
						options={{ alt_text: options?.top_logo?.link?.title, svg: options?.top_logo?.logo }}
						onClick={goGoJuice}
						className="logo-svg"
					/>
				</Link>
			</div>

			<div className="footer-main-links container">
				<div className="row">
					<div className="footer-pre-copy col-lg-3">
						{pre_copy.map((val, i) => (
							<div className="footer-pre-copy-block" key={i}>
								{val}
							</div>
						))}
					</div>

					<FooterLinkCol columns={options?.center_columns} />

					{/* The right column */}
					<div className="footer-post-copy col-lg-2">
						{post_copy.map((val, i) => (
							<div className="footer-post-copy-block" key={i}>
								{val}
							</div>
						))}
					</div>
				</div>
			</div>

			<div className="bottom-logos">
				<div className="row-1">
					{options?.socials_icons?.map((si, i) => (
						<Link key={`social-link-${si.link.href}`} to={si.link.href} target="_blank" aria-label={si.link.title}>
							<SvgImage options={{ alt_text: si.link.title, svg: si.icon }} />
						</Link>
					))}
				</div>

				<div className="row-2">
					{options?.bank_logos?.map((bl, i) => (
						<SvgImage options={{ alt_text: bl.alt_text, svg: bl.logo }} key={`bank-logo-${bl.logo.url}`} />
					))}
				</div>

				<div className="row-3">&copy; Bank OZK {new Date().getFullYear()}</div>
			</div>
		</div>
	);
};

/**
 * @typedef FooterLinkColProps
 * @property {FooterCenterColumn[]} columns
 */
/** @type {import('~/types/React').ReactFC<FooterLinkColProps>} */
const FooterLinkCol = ({ columns }) => {
	/** @type {import('../SiteLinks').LinkItem[]} */
	const linkItems = columns.map((col) => ({
		name: col.header,
		children: col.link.map((l) => ({
			name: l.title,
			link: l.href,
		})),
	}));

	return (
		<React.Fragment>
			{/* The middle 3 columns on desktop */}
			{columns.map((col, i) => (
				<div className="link-col col-lg d-none d-lg-block" key={i}>
					<h1 className="link-header" dangerouslySetInnerHTML={{ __html: col.header }} />
					<ul className="link-list">
						{col.link.map((l, j) => (
							<li key={j}>
								<Link to={l.href}>{l.title}</Link>
							</li>
						))}
					</ul>
				</div>
			))}

			{/* The Accordion menu on mobile */}
			<AccordionMenu className="link-menu-mobile d-lg-none" items={linkItems} />
		</React.Fragment>
	);
};

export default Footer;
